import type { CompetitorSections } from "../../types"

import { COMPANY_NAME } from "../../utils/branding"

/**
 * Competitor Plan — section catalogue.
 *
 * Titles are the exact Word/handover strings (Competitor template); headings
 * that name the tenant's own company interpolate COMPANY_NAME
 * (`organization_name`, #2768). §06 (existing relationships with the tenant's
 * customers/prospects) is MANUAL only: Expand but no Update — the generic
 * Update endpoint 400s for `nolatoRelationships` (the field key is a
 * wire/storage contract and keeps its historical name).
 */

export type CompetitorSectionKey = keyof CompetitorSections

export type CompetitorSectionKind = "prose" | "bullets" | "strengthsWeaknesses" | "manual"

export type CompetitorSectionDef = {
  key: CompetitorSectionKey
  no: string
  /** Exact Word/handover title. */
  title: string
  kind: CompetitorSectionKind
  fieldKey: CompetitorSectionKey
  /** AI-updatable (Update + Expand). False for the manual §06 tile (Expand only). */
  updatable: boolean
  canvas: { colSpan: number; rowSpan: number }
}

export const COMPETITOR_SECTIONS: CompetitorSectionDef[] = [
  {
    key: "overview",
    no: "01",
    title: "Competitor overview",
    kind: "prose",
    fieldKey: "overview",
    updatable: true,
    canvas: { colSpan: 8, rowSpan: 1 },
  },
  {
    key: "businessModel",
    no: "02",
    title: "Business Model",
    kind: "prose",
    fieldKey: "businessModel",
    updatable: true,
    canvas: { colSpan: 4, rowSpan: 1 },
  },
  {
    key: "marketOverview",
    no: "03",
    title: "Market Overview",
    kind: "prose",
    fieldKey: "marketOverview",
    updatable: true,
    canvas: { colSpan: 6, rowSpan: 1 },
  },
  {
    key: "keyActivities",
    no: "04",
    title: "Key activities",
    kind: "bullets",
    fieldKey: "keyActivities",
    updatable: true,
    canvas: { colSpan: 6, rowSpan: 1 },
  },
  {
    key: "strengthsWeaknesses",
    no: "05",
    title: `Strengths vs. Weaknesses (vs ${COMPANY_NAME})`,
    kind: "strengthsWeaknesses",
    fieldKey: "strengthsWeaknesses",
    updatable: true,
    canvas: { colSpan: 8, rowSpan: 1 },
  },
  {
    key: "nolatoRelationships",
    no: "06",
    title: `Existing relationships with ${COMPANY_NAME} customers/prospects`,
    kind: "manual",
    fieldKey: "nolatoRelationships",
    updatable: false,
    canvas: { colSpan: 4, rowSpan: 1 },
  },
]
